/**
 * ST-05 (AIVA-6805) — the native PowerPoint actions "All actions" lists.
 * Pure, so every rule is unit-testable. Ranking as the user types is
 * Deckorate's filterAndRank, shared with its design actions.
 *
 * Three groups, in this order:
 *  - Deck: the built-ins that run from one click. Their prompts are the spec's
 *    accepted phrasings, so the local handlers answer them exactly as when typed.
 *  - Skills: the persona's PowerPoint slash skills (from the same cache as the
 *    `/` menu, user-disabled ones already removed).
 *  - AiVA: Actions, New chat, Settings.
 *
 * Modules are deliberately not listed: they are whatever Studio grants the
 * persona on the channel (email and calendar included), and the MORE tab
 * already lists them.
 */

import type { PaletteItem } from "../types";

export interface PaletteSkill {
  readonly slash: string;
  readonly label: string;
  readonly description: string;
}

export interface PaletteSources {
  readonly skills: readonly PaletteSkill[];
}

const DECK_ITEMS: readonly PaletteItem[] = [
  {
    id: "deck.summarise",
    label: "Summarise the entire deck",
    description: "A short summary of every slide",
    group: "Deck",
    keywords: ["summary", "summarize", "tldr", "overview"],
    action: { kind: "prompt", text: "Summarise this deck" },
  },
  {
    id: "deck.speaker-notes",
    label: "List speaker notes across slides",
    description: "Every slide's notes, slide by slide",
    group: "Deck",
    keywords: ["notes", "presenter"],
    action: { kind: "prompt", text: "Show me the speaker notes" },
  },
  {
    id: "deck.slide-content",
    label: "Show what's on the current slide",
    description: "The shapes on this slide and their text",
    group: "Deck",
    keywords: ["shapes", "describe", "current slide"],
    action: { kind: "prompt", text: "What's on this slide?" },
  },
];

/** SC-01's slash skill (ppt_draft_content). When the persona has it, the
 * built-in Draft entry uses it rather than listing the same job twice. */
const DRAFT_SLASH = "/draft";

function draftItem(hasDraftSkill: boolean): PaletteItem {
  return {
    id: "deck.draft",
    label: "Draft slide content from a brief",
    description: "Type a brief, then send it",
    group: "Deck",
    keywords: ["write", "bullets", "title", DRAFT_SLASH],
    // The slash forces the drafting skill; without it the router picks one.
    action: { kind: "prefill", text: hasDraftSkill ? `${DRAFT_SLASH} ` : "Draft slide content: " },
  };
}

const AIVA_ITEMS: readonly PaletteItem[] = [
  {
    id: "aiva.actions",
    label: "Open Actions",
    description: "Your next best actions",
    group: "AiVA",
    keywords: ["nba", "tasks"],
    action: { kind: "open-actions" },
  },
  {
    id: "aiva.new-chat",
    label: "New chat",
    description: "Start a fresh conversation",
    group: "AiVA",
    keywords: ["clear", "reset", "conversation"],
    action: { kind: "new-chat" },
  },
  {
    id: "aiva.settings",
    label: "Settings",
    description: "Modules, skills and connections",
    group: "AiVA",
    keywords: ["preferences", "options", "skills", "connections"],
    action: { kind: "settings" },
  },
];

function skillItem(skill: PaletteSkill): PaletteItem {
  return {
    id: `skill.${skill.slash}`,
    label: skill.slash,
    description: skill.description || skill.label,
    group: "Skills",
    keywords: [skill.label],
    action: { kind: "prefill", text: `${skill.slash} ` },
  };
}

export function buildPaletteItems(sources: PaletteSources): PaletteItem[] {
  const hasDraftSkill = sources.skills.some((s) => s.slash === DRAFT_SLASH);
  const otherSkills = sources.skills.filter((s) => s.slash !== DRAFT_SLASH);
  return [...DECK_ITEMS, draftItem(hasDraftSkill), ...otherSkills.map(skillItem), ...AIVA_ITEMS];
}
